import styled from '@emotion/styled';
import chroma from 'chroma-js';
import React, { useEffect, useState } from 'react';
import { RgbColorPicker, RgbaColorPicker } from 'react-colorful';
import { Input } from '../input';
import { Dropdown } from '../dropdown';
import { colors, border_radius_styles_with_size } from '../../foundation';

interface HslColor {
  hue: number;
  saturation: number;
  lightness: number;
}

interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

interface RgbaColor extends RgbColor {
  alpha: number;
}

import { RgbController } from './RgbController';
import { HslController } from './HslController';
import { toHexCode, toRegular } from './utils';

const DEFAULT_COLOR = colors.black;

type CodeType = 'HEX' | 'RGB' | 'HSL';
const COLOR_OPTION: Array<{ label: string; value: CodeType }> = [
  {
    label: 'HEX',
    value: 'HEX',
  },
  {
    label: 'RGB',
    value: 'RGB',
  },
  {
    label: 'HSL',
    value: 'HSL',
  },
];

interface ControllerProps {
  code?: string;
  support_alpha?: boolean;
  onCodeChange(code: string): void;
  hueOnly?: boolean;
  zIndex?: number;
}

export const Controller: React.FC<ControllerProps> = ({
  code = DEFAULT_COLOR,
  support_alpha,
  onCodeChange,
  hueOnly,
  zIndex,
}) => {
  const [code_type, setCodeType] = useState<CodeType>(hueOnly ? 'HSL' : 'HEX');
  const [hex_input_value, setHexInputValue] = useState<string>(code.replace('#', '').substring(0, 6));
  const rgba: RgbaColor = (() => {
    try {
      const [r, g, b, a] = chroma(code).rgba();
      return { red: r, green: g, blue: b, alpha: a };
    } catch (e) {
      return { red: 0, green: 0, blue: 0, alpha: 1 };
    }
  })();

  const setColor = (value: string) => {
    onCodeChange(value.toUpperCase());
  };

  const setHexValue = (code: string) => {
    const value = toHexCode(code)?.substring(0, 6);
    if (value && value.length === 6) {
      setColor(
        chroma('#' + value)
          .alpha(rgba.alpha ?? 1)
          .hex(),
      );
    } else {
      // @ts-expect-error: TS2345
      setHexInputValue(value);
    }
  };

  const setRgbValue = (value: RgbColor, alpha?: number) => {
    setColor(
      chroma
        .rgb(value.red, value.green, value.blue)
        .alpha(alpha ?? 1)
        .hex(),
    );
  };

  const setHslValue = (value: HslColor) => {
    setColor(
      chroma
        .hsl(value.hue, value.saturation, value.lightness)
        .alpha(rgba.alpha ?? 1)
        .hex(),
    );
  };

  useEffect(() => {
    setHexInputValue(code.replace('#', '').substring(0, 6));
  }, [code]);

  return (
    <Container data-testid='color-picker-controller' hueOnly={hueOnly}>
      {support_alpha ? (
        <RgbaColorPicker
          color={{ r: rgba.red, g: rgba.green, b: rgba.blue, a: rgba.alpha ?? 1 }}
          onChange={(value) => {
            setRgbValue({ red: value.r, green: value.g, blue: value.b }, value.a > -1 ? value.a : 1);
          }}
        />
      ) : (
        <RgbColorPicker
          color={{ r: rgba.red, g: rgba.green, b: rgba.blue }}
          onChange={(rgb) => {
            setRgbValue({ red: rgb.r, green: rgb.g, blue: rgb.b });
          }}
        />
      )}
      <CodeContainer support_alpha={Boolean(support_alpha)}>
        <Dropdown
          options={COLOR_OPTION}
          size='xsmall'
          value={code_type}
          zIndex={zIndex}
          onChange={(code_type) => setCodeType(code_type)}
          disabled={hueOnly}
        />
        {code_type === 'HEX' && (
          <Input
            size='xsmall'
            value={hex_input_value}
            onChange={(e) => setHexValue(e.target.value)}
            onBlur={(e) => {
              const value = e.target.value;
              setHexValue(value.length < 6 ? toRegular(value) : value);
            }}
            maxLength={7}
            width='auto'
            data-testid='color-picker-controller-hex-input'
          />
        )}
        {code_type === 'RGB' && <RgbController value={code} onChange={setRgbValue} />}
        {code_type === 'HSL' && <HslController value={code} onChange={setHslValue} hueOnly={hueOnly} />}
        {support_alpha && (
          <Input
            size='xsmall'
            maxLength={4}
            value={`${Math.round((rgba.alpha ?? 1) * 100)}%`}
            disabled={hueOnly}
            onChange={(event) => {
              setRgbValue(rgba, Number(event.target.value.replace('%', '')) / 100);
            }}
            data-testid='color-picker-controller-alpha-input'
          />
        )}
      </CodeContainer>
    </Container>
  );
};

const Container = styled.div<{ hueOnly?: boolean }>`
  display: flex;
  flex-direction: column;
  row-gap: 16px;

  .react-colorful {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    width: auto;
    height: fit-content;
    &__saturation {
      ${({ hueOnly }) => hueOnly && 'display: none;'}
      border-radius: ${border_radius_styles_with_size.small}px;
      box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.25) inset;
      border-bottom: 0;
      height: 160px;
    }
    &__hue,
    &__alpha {
      box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.25) inset;
      height: 9px;
      border-radius: 9px;
    }
    &__alpha {
      ${({ hueOnly }) => hueOnly && 'display: none;'}
    }
    &__saturation-pointer,
    &__hue-pointer,
    &__alpha-pointer {
      width: 9px;
      height: 9px;
      border: 2px solid ${colors.white};
      border-radius: 50%;
      box-sizing: content-box;
      box-shadow: 0px 0px 0px 0.6px rgba(0, 0, 0, 0.2);
    }
  }
`;

const CodeContainer = styled.div<{ support_alpha: boolean }>`
  display: grid;
  column-gap: 4px;
  grid-template-columns: 64px auto ${({ support_alpha }) => support_alpha && '52px'};
`;
